// src/components/PaymentModal/PaymentModal.js
import React, { useState, useEffect } from "react";
import "./PaymentModal.css";

import CustomModal from "../../components/CustomModal/CustomModal";

const PaymentModal = ({ isOpen, onClose, onPaymentSubmit }) => {
  const [cardholderName, setCardholderName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiryMonth, setExpiryMonth] = useState("");
  const [expiryYear, setExpiryYear] = useState("");
  const [cvv, setCvv] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [modalMessage, setModalMessage] = useState({
    title: "",
    message: "",
    type: "",
  });
  const [showInternalModal, setShowInternalModal] = useState(false);
  const [cardType, setCardType] = useState("Unknown"); // Nuevo estado para el tipo de tarjeta

  useEffect(() => {
    const modal = document.querySelector(".modal-overlay-custom");
    if (modal) {
      if (isOpen) {
        modal.classList.add("show");
      } else {
        modal.classList.remove("show");
      }
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  // Función para detectar el tipo de tarjeta basándose en el número
  const detectCardType = (number) => {
    // Patrones de BIN para tarjetas populares
    const visaPattern = /^4/;
    const mastercardPattern = /^5[1-5]/;
    const amexPattern = /^3[47]/;

    if (number.match(visaPattern)) {
      setCardType("Visa");
    } else if (number.match(mastercardPattern)) {
      setCardType("Mastercard");
    } else if (number.match(amexPattern)) {
      setCardType("American Express");
    } else {
      setCardType("Unknown");
    }
  };

  const handleCardNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Solo números
    setCardNumber(value);
    detectCardType(value);
  };

  const getCardLogo = () => {
    switch (cardType) {
      case "Visa":
        return "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/200px-Visa_Inc._logo.svg.png";
      case "Mastercard":
        return "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo-png-transparent.png/320px-Mastercard-logo-png-transparent.png";
      case "American Express":
        return "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/American_Express_logo_light.svg/200px-American_Express_logo_light.svg.png";
      default:
        return null;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      setModalMessage({
        title: "Atención",
        message: "Por favor, acepta los términos y condiciones para continuar.",
        type: "error",
      });
      setShowInternalModal(true);
      return;
    }

    if (cardType === "Unknown") {
      setModalMessage({
        title: "Error de validación",
        message:
          "Tipo de tarjeta desconocido. Por favor, ingrese un número de tarjeta válido.",
        type: "error",
      });
      setShowInternalModal(true);
      return;
    }

    // Se crea un objeto con todos los datos del formulario
    const paymentData = {
      cardholderName,
      cardNumber,
      expiryMonth,
      expiryYear,
      cvv,
      cardType, // Se agrega el tipo de tarjeta
    };

    // Se llama a la función onPaymentSubmit del componente padre con el objeto de datos
    onPaymentSubmit(paymentData);
  };

  return (
    <>
      <style>
        {`
          .modal-overlay-custom {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.3s ease, visibility 0.3s ease;
          }
          
          .modal-overlay-custom.show {
            opacity: 1;
            visibility: visible;
          }

          .modal-content-wrapper-custom {
            position: relative;
            width: 100%;
            max-width: 500px;
            margin: 20px;
            transform: translateY(-20px);
            transition: transform 0.3s ease;
          }

          .modal-overlay-custom.show .modal-content-wrapper-custom {
            transform: translateY(0);
          }

          .payment-icon {
            height: 24px;
            width: auto;
          }

          .btn-close-custom {
            background: none;
            border: none;
            font-size: 1.5rem;
            color: #6c757d;
            cursor: pointer;
            padding: 0.5rem;
          }
        `}
      </style>
      <div className="modal-overlay-custom">
        <div className="modal-content-wrapper-custom">
          <div className="modal-content-container-custom p-4 rounded-3 shadow-lg">
            <div className="modal-header-custom d-flex justify-content-between align-items-center mb-3">
              <h5 className="modal-title-custom m-0">
                <i className="bi bi-info-circle-fill text-info me-2"></i>
                Formulario de Pago
              </h5>
              <button
                type="button"
                className="btn-close-custom"
                aria-label="Close"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body-custom">
              <div className="payment-modal-content">
                <div className="d-flex align-items-center mb-3">
                  <input
                    type="radio"
                    id="tarjetaCredito"
                    name="payment-method"
                    className="form-check-input me-2"
                    defaultChecked
                  />
                  <label
                    htmlFor="tarjetaCredito"
                    className="form-check-label me-3"
                  >
                    Tarjetas de crédito o débito
                  </label>
                  <img
                    src="https://assets.visaeurope.com/frontend/visa-logo-white-background.svg"
                    alt="Visa"
                    className="payment-icon"
                  />
                  <img
                    src="https://assets.mastercard.com/content/dam/public/brandcenter/mastercard-brandcenter-logo-blue-and-red.svg"
                    alt="MasterCard"
                    className="payment-icon mx-2"
                  />
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="mb-3 form-floating">
                    <input
                      type="text"
                      id="cardholderName"
                      className="form-control"
                      placeholder=" "
                      value={cardholderName}
                      onChange={(e) => setCardholderName(e.target.value)}
                      required
                    />
                    <label htmlFor="cardholderName">
                      Nombre que figura en la tarjeta
                    </label>
                  </div>
                  <div
                    className="mb-3 form-floating"
                    style={{ position: "relative" }}
                  >
                    <input
                      type="text"
                      id="cardNumber"
                      className="form-control"
                      placeholder=" "
                      value={cardNumber}
                      onChange={handleCardNumberChange}
                      required
                    />
                    <label htmlFor="cardNumber">Número de la tarjeta</label>
                    {getCardLogo() && (
                      <img
                        src={getCardLogo()}
                        alt={`${cardType} logo`}
                        className="card-logo"
                      />
                    )}
                  </div>
                  <div className="row">
                    <div className="col-6 mb-3">
                      <div className="form-floating">
                        <input
                          type="text"
                          id="expiryMonth"
                          className="form-control"
                          placeholder=" "
                          value={expiryMonth}
                          onChange={(e) => setExpiryMonth(e.target.value)}
                          required
                        />
                        <label htmlFor="expiryMonth">Mes de vencimiento</label>
                      </div>
                      <small className="text-muted">mm</small>
                    </div>
                    <div className="col-6 mb-3">
                      <div className="form-floating">
                        <input
                          type="text"
                          id="expiryYear"
                          className="form-control"
                          placeholder=" "
                          value={expiryYear}
                          onChange={(e) => setExpiryYear(e.target.value)}
                          required
                        />
                        <label htmlFor="expiryYear">Año de vencimiento</label>
                      </div>
                      <small className="text-muted">yyyy</small>
                    </div>
                    <div className="col-6 mb-3">
                      <div className="form-floating">
                        <input
                          type="text"
                          id="cvv"
                          className="form-control"
                          placeholder=" "
                          value={cvv}
                          onChange={(e) => setCvv(e.target.value)}
                          required
                        />
                        <label htmlFor="cvv">CVV</label>
                      </div>
                    </div>
                  </div>
                  <div className="form-check mb-4">
                    <input
                      type="checkbox"
                      id="termsAccepted"
                      className="form-check-input"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      required
                    />
                    <label
                      htmlFor="termsAccepted"
                      className="form-check-label small-text"
                    >
                      He leído y acepto los términos de uso, reglas de vuelo y
                      política de privacidad
                    </label>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary btn-pay-now w-100"
                  >
                    Pagar ahora
                  </button>
                </form>

                <hr className="my-4" />

                <div className="d-flex align-items-center mb-3">
                  <input
                    type="radio"
                    id="paypal"
                    name="payment-method"
                    className="form-check-input me-2"
                  />
                  <label htmlFor="paypal" className="form-check-label me-3">
                    Pay with Paypal
                  </label>
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg"
                    alt="PayPal"
                    className="payment-icon"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CustomModal
        isOpen={showInternalModal}
        title={modalMessage.title}
        message={modalMessage.message}
        type={modalMessage.type}
        onClose={() => setShowInternalModal(false)}
      />
    </>
  );
};

export default PaymentModal;
